import { gql } from '@apollo/client';

/**
 * Query to get allocation batches for a snapshot with their allocations and instrument data
 *
 * This query fetches current_holdings, universe, benchmark, and proposed_holdings batches for a given snapshot.
 * The data structure allows us to join different allocation types by ISIN.
 */
export const GET_EQALLOCATION_DATA = gql`
  query GetEQAllocationData($snapshotId: uuid!) {
    # Get current holdings batch
    currentHoldings: EQSnapshotAllocationLink(
      where: {
        snapshotId: { _eq: $snapshotId }
        allocation: { type: { _eq: "current_holdings" } }
      }
    ) {
      allocation {
        id
        type
        asOfDatetime
        allocations(order_by: { percent: desc }) {
          id
          isin
          percent
          value
          quantity
          EQInstrument {
            full_ticker
            name
            isin
            sector
          }
        }
      }
    }

    # Get universe batch
    universe: EQSnapshotAllocationLink(
      where: {
        snapshotId: { _eq: $snapshotId }
        allocation: { type: { _eq: "universe" } }
      }
    ) {
      allocation {
        id
        type
        asOfDatetime
        allocations(order_by: { percent: desc }) {
          id
          isin
          percent
          value
          quantity
          EQInstrument {
            full_ticker
            name
            isin
            sector
          }
        }
      }
    }

    # Get benchmark batch
    benchmark: EQSnapshotAllocationLink(
      where: {
        snapshotId: { _eq: $snapshotId }
        allocation: { type: { _eq: "benchmark" } }
      }
    ) {
      allocation {
        id
        type
        asOfDatetime
        allocations(order_by: { percent: desc }) {
          id
          isin
          percent
          value
          quantity
          EQInstrument {
            full_ticker
            name
            isin
            sector
          }
        }
      }
    }

    # Get proposed holdings batch
    proposedHoldings: EQSnapshotAllocationLink(
      where: {
        snapshotId: { _eq: $snapshotId }
        allocation: { type: { _eq: "proposed" } }
      }
    ) {
      allocation {
        id
        type
        asOfDatetime
        allocations(order_by: { percent: desc }) {
          id
          isin
          percent
          value
          quantity
          EQInstrument {
            full_ticker
            name
            isin
            sector
          }
        }
      }
    }
  }
`;

/**
 * Query to get allocation batch by ID with all allocations and instrument data
 */
export const GET_ALLOCATION_BATCH = gql`
  query GetAllocationBatch($batchId: uuid!) {
    EQAllocationBatch(where: { id: { _eq: $batchId } }) {
      id
      portfolioId
      type
      asOfDatetime
      metadata
      createdAt
      allocations(order_by: { percent: desc }) {
        id
        isin
        percent
        value
        quantity
        cost_basis
        unrealized_pnl
        EQInstrument {
          full_ticker
          name
          isin
          sector
        }
      }
    }
  }
`;

/**
 * Query to get all allocation batches for a portfolio
 */
export const GET_PORTFOLIO_ALLOCATION_BATCHES = gql`
  query GetPortfolioAllocationBatches($portfolioId: uuid!) {
    EQAllocationBatch(
      where: { portfolioId: { _eq: $portfolioId } }
      order_by: { createdAt: desc }
    ) {
      id
      type
      asOfDatetime
      metadata
      createdAt
      allocations_aggregate {
        aggregate {
          count
        }
      }
    }
  }
`;
